import { env } from "../config/env";

/**
 * Utilidades para llamadas a la API
 */

interface RequestConfig extends RequestInit {
  timeout?: number;
}

class ApiError extends Error {
  status: number;
  statusText: string;

  constructor(
    status: number,
    statusText: string,
    message: string
  ) {
    super(message);
    this.status = status;
    this.statusText = statusText;
    this.name = "ApiError";
  }
}

/**
 * Cliente API principal
 */
export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;
  private isRefreshing: boolean = false;

  constructor(baseUrl: string = env.apiUrl) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      "Content-Type": "application/json",
    };
  }

  /**
   * Wrapper de fetch con timeout, manejo de errores y renovación automática de token
   */
  private async fetchWithTimeout(
    url: string,
    config: RequestConfig = {}
  ): Promise<Response> {
    const { timeout = 30000, ...fetchConfig } = config;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchConfig,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Si la respuesta es 401 y no estamos renovando, intentar renovar el token
      if (response.status === 401 && !this.isRefreshing) {
        const renewed = await this.renewToken();
        if (renewed) {
          // Reintentar la petición con el nuevo token
          const retryController = new AbortController();
          const retryTimeoutId = setTimeout(() => retryController.abort(), timeout);

          try {
            const retryResponse = await fetch(url, {
              ...fetchConfig,
              signal: retryController.signal,
              headers: this.getHeaders(fetchConfig.headers as HeadersInit),
            });

            clearTimeout(retryTimeoutId);
            return retryResponse;
          } catch (retryError) {
            clearTimeout(retryTimeoutId);
            if (retryError instanceof Error && retryError.name === "AbortError") {
              throw new ApiError(408, "Request Timeout", "La solicitud ha excedido el tiempo límite");
            }
            throw retryError;
          }
        }
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new ApiError(408, "Request Timeout", "La solicitud ha excedido el tiempo límite");
      }
      throw error;
    }
  }

  /**
   * Obtiene el token de autenticación del localStorage
   */
  private getStoreUser(): string | null {
    return localStorage.getItem("app-storage");
  }
  /**
   * Construye los headers de la petición
   */
  private getHeaders(customHeaders?: HeadersInit): HeadersInit {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    const store = this.getStoreUser();
    const dataStore = store ? JSON.parse(store) : null;

    if (dataStore) {
      return {
        ...headers,
        Authorization: `Bearer ${dataStore?.state?.user?.token}`,
        user: dataStore?.state?.user?.name,
      };
    }

    return headers;
  }

  /**
   * Renueva el token de autenticación
   */
  private async renewToken(): Promise<boolean> {
    if (this.isRefreshing) {
      return false;
    }

    this.isRefreshing = true;

    try {
      const store = this.getStoreUser();
      const dataStore = store ? JSON.parse(store) : null;
      if (!dataStore?.state?.user?.token) {
        return false;
      }

      const url = `${this.baseUrl}RenewToken`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: `"${dataStore.state.user.token}"`
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();

      // Actualizar el token en el store
      if (data?.data?.jwtToken) {

        dataStore.state.user.token = data.data.jwtToken;
        localStorage.setItem("app-storage", JSON.stringify(dataStore));
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error renovando token:", error);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Maneja las respuestas de la API
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw errorData;

      // throw new ApiError(
      //   errorData?.statusCode || response.status,
      //   errorData?.resultError?.title || response.statusText,
      //   errorData?.resultError?.message || "Error en la solicitud"
      // );
    }

    // Si la respuesta es 204 No Content, retornar objeto vacío
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await this.fetchWithTimeout(url, {
      ...config,
      method: "GET",
      headers: this.getHeaders(config?.headers),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await this.fetchWithTimeout(url, {
      ...config,
      method: "POST",
      headers: this.getHeaders(config?.headers),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await this.fetchWithTimeout(url, {
      ...config,
      method: "PUT",
      headers: this.getHeaders(config?.headers),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await this.fetchWithTimeout(url, {
      ...config,
      method: "PATCH",
      headers: this.getHeaders(config?.headers),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await this.fetchWithTimeout(url, {
      ...config,
      method: "DELETE",
      headers: this.getHeaders(config?.headers),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Upload de archivos
   */
  async upload<T>(
    endpoint: string,
    file: File,
    fieldName: string = "file",
    additionalData?: Record<string, string>
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const formData = new FormData();
    formData.append(fieldName, file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const user = this.getStoreUser();
    const dataStore = user ? JSON.parse(user) : null;
    const headers: HeadersInit = {};
    if (dataStore) {
      headers.Authorization = `Bearer ${dataStore?.state?.user?.token}`;
    }

    const response = await this.fetchWithTimeout(url, {
      method: "POST",
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }
}

// Instancia global del cliente API
export const apiClient = new ApiClient();

// Exports adicionales
export { ApiError };
